import { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, ImagePlus, Undo2, Redo2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ToolButton = ({ onClick, active, label, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    className={`p-2 transition-colors ${active ? "bg-forest text-bone" : "text-forest hover:bg-bonealt"}`}
  >
    {children}
  </button>
);

export const RichEditor = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB.");
      return;
    }
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/uploads", data);
      editor.chain().focus().setImage({ src: res.url }).run();
      toast.success("Photo added to story.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!editor) return <div className="border border-forest/25 bg-bone h-64 animate-pulse" />;

  return (
    <div className="border border-forest/25 bg-bone focus-within:border-terra transition" data-testid="post-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-forest/15 px-2 py-1.5" data-testid="rich-editor-toolbar">
        <ToolButton testid="editor-bold" label="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold size={15} /></ToolButton>
        <ToolButton testid="editor-italic" label="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic size={15} /></ToolButton>
        <span className="w-px h-5 bg-forest/15 mx-1" />
        <ToolButton testid="editor-h2" label="Heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 size={15} /></ToolButton>
        <ToolButton testid="editor-h3" label="Subheading" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 size={15} /></ToolButton>
        <span className="w-px h-5 bg-forest/15 mx-1" />
        <ToolButton testid="editor-bullet-list" label="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List size={15} /></ToolButton>
        <ToolButton testid="editor-ordered-list" label="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered size={15} /></ToolButton>
        <ToolButton testid="editor-quote" label="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}><Quote size={15} /></ToolButton>
        <span className="w-px h-5 bg-forest/15 mx-1" />
        <ToolButton testid="editor-image" label="Insert photo" onClick={() => fileRef.current?.click()} active={false}><ImagePlus size={15} /></ToolButton>
        <span className="w-px h-5 bg-forest/15 mx-1" />
        <ToolButton testid="editor-undo" label="Undo" onClick={() => editor.chain().focus().undo().run()} active={false}><Undo2 size={15} /></ToolButton>
        <ToolButton testid="editor-redo" label="Redo" onClick={() => editor.chain().focus().redo().run()} active={false}><Redo2 size={15} /></ToolButton>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="editor-image-input" />
      </div>
      <EditorContent editor={editor} className="rich-editor-content" data-testid="editor-content" />
    </div>
  );
};
